import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  FileText, Play, CheckCircle2, Monitor, Key,
  Send, Download, Sparkles, Clock, Loader, XCircle,
  RefreshCw, Trash2, AlertTriangle, ShieldCheck, X
} from 'lucide-react'
import {
  getClients, getReports, generateReport, downloadReport, approveReport, dispatchReport,
} from '../api'

export default function ReportReview() {
  const [clients, setClients] = useState([])
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showGenModal, setShowGenModal] = useState(false)
  const [selectedClient, setSelectedClient] = useState('')
  const [execMode, setExecMode] = useState('live_api')
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState(null)
  const [activeReportId, setActiveReportId] = useState(null)
  const [downloadError, setDownloadError] = useState(null)
  const [lifecycleBusy, setLifecycleBusy] = useState(null)
  const [lifecycleError, setLifecycleError] = useState(null)
  const [lifecycleNotice, setLifecycleNotice] = useState(null)

  // The selection is held as an id and the row is looked up from the current
  // list. Previously the whole row was held in state while `fetchData` was a
  // `useCallback([])` that read `activeReport` through a stale closure, so the
  // guard `!activeReport` was always true and every refresh silently reset the
  // selection to the first report.
  const activeReport = useMemo(
    () => reports.find((r) => r.id === activeReportId) || null,
    [reports, activeReportId],
  )

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [clientData, reportData] = await Promise.all([getClients(), getReports()])
      setClients(clientData.clients || [])
      const reportList = reportData.reports || []
      setReports(reportList)
      setActiveReportId((current) => {
        if (current && reportList.some((r) => r.id === current)) return current
        return reportList.length > 0 ? reportList[0].id : null
      })
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const handleStartGeneration = async () => {
    if (!selectedClient) {
      setGenError('Select a client account first.')
      return
    }
    setGenerating(true)
    setGenError(null)

    try {
      // No simulated progress steps. The previous implementation advanced a
      // three-stage "Querying CloudWatch / Synthesizing with AI / Compiling"
      // display on 1.5s and 3s timers that had no relationship to the work, so a
      // 40-second collection showed "Compiling" for 37 of them.
      const result = await generateReport(parseInt(selectedClient, 10), execMode)
      const reportData = await getReports()
      const reportList = reportData.reports || []
      setReports(reportList)
      setActiveReportId(result.report_id ?? (reportList[0]?.id ?? null))
      setShowGenModal(false)
      setSelectedClient('')
      if (result.warnings?.length) {
        setLifecycleNotice(
          `Report generated with ${result.warnings.length} collection warning(s). See the report provenance section.`,
        )
      }
    } catch (err) {
      setGenError(err.message)
    } finally {
      setGenerating(false)
    }
  }

  const handleDownload = async (reportId, docType) => {
    setDownloadError(null)
    try {
      await downloadReport(reportId, docType)
    } catch (err) {
      setDownloadError(`Download failed: ${err.message}`)
    }
  }

  const refreshReports = async () => {
    const reportData = await getReports()
    setReports(reportData.reports || [])
  }

  const handleApprove = async () => {
    if (!activeReport) return
    setLifecycleBusy('approve')
    setLifecycleError(null)
    setLifecycleNotice(null)
    try {
      await approveReport(activeReport.id)
      await refreshReports()
      setLifecycleNotice('Sign-off recorded. The report can now be sent.')
    } catch (err) {
      setLifecycleError(err.message)
    } finally {
      setLifecycleBusy(null)
    }
  }

  const handleDispatch = async () => {
    if (!activeReport) return
    setLifecycleBusy('dispatch')
    setLifecycleError(null)
    setLifecycleNotice(null)
    try {
      const result = await dispatchReport(activeReport.id)
      await refreshReports()
      const skipped = result.skipped_attachments?.length
      setLifecycleNotice(
        `Sent to ${result.recipients.length} recipient(s) with ${result.attachment_count} attachment(s)`
        + (skipped ? `; ${skipped} attachment(s) were missing on disk.` : '.'),
      )
    } catch (err) {
      setLifecycleError(err.message)
    } finally {
      setLifecycleBusy(null)
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280, gap: 12 }}>
      <Loader size={36} className="spin" style={{ color: 'var(--brand-blue)' }} />
      <div className="text-muted">Loading reports archive...</div>
    </div>
  )

  if (error) return (
    <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
      <XCircle size={32} style={{ color: 'var(--brand-coral)', margin: '0 auto 12px' }} />
      <h3 style={{ color: 'var(--brand-coral)', fontFamily: 'var(--font-serif)', marginBottom: 8 }}>Backend Connection Failed</h3>
      <p className="text-muted text-sm" style={{ marginBottom: 16 }}>{error}</p>
      <button className="btn btn-primary" onClick={fetchData}>Retry Connection</button>
    </div>
  )

  return (
    <div className="animate-in">
      <div className="flex-between mb-3">
        <div>
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '22px', fontWeight: 800, color: 'var(--ink-900)' }}>
            Human-In-The-Loop Report Studio
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--ink-600)', marginTop: 2 }}>
            Review AI-synthesized executive reports from live AWS telemetry — approve, download, and dispatch.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={fetchData}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setShowGenModal(true)}>
            <Play size={15} /> Generate Live AWS Report
          </button>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="card empty-state" style={{ padding: '60px 20px' }}>
          <FileText size={42} style={{ color: 'var(--brand-blue)', margin: '0 auto 12px' }} />
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, color: 'var(--ink-900)', marginBottom: 6 }}>No Reports Generated Yet</h3>
          <p style={{ fontSize: 13, color: 'var(--ink-500)', maxWidth: 440, margin: '0 auto 18px' }}>
            Select an onboarded client to run telemetry extraction and build letterhead Word (.docx) and Excel (.xlsx) packages.
          </p>
          <button className="btn btn-primary" onClick={() => setShowGenModal(true)}>
            <Play size={15} /> Generate Live AWS Report
          </button>
        </div>
      ) : (
        <div className="grid-2-1">
          {/* Main Report Preview */}
          <div>
            {activeReport && (
              <div className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* The backend lifecycle is draft -> approved -> dispatched,
                        with dispatch_failed as a terminal error. The old badge only
                        knew a 'sent' status that the API never produced, so every
                        report read as "DRAFT FOR REVIEW" forever. */}
                    <span
                      className={`badge ${
                        activeReport.status === 'dispatched'
                          ? 'badge-ok'
                          : activeReport.status === 'dispatch_failed'
                            ? 'badge-alarm'
                            : 'badge-warning'
                      }`}
                    >
                      {{
                        dispatched: 'SENT TO CLIENT',
                        approved: 'APPROVED — READY TO SEND',
                        dispatch_failed: 'DELIVERY FAILED',
                        draft: 'DRAFT — AWAITING REVIEW',
                      }[activeReport.status] || String(activeReport.status || 'UNKNOWN').toUpperCase()}
                    </span>
                    <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-900)' }}>
                      {activeReport.client_name}
                    </h3>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => handleDownload(activeReport.id, 'docx')}>
                      <Download size={13} /> Word (.docx)
                    </button>
                    <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => handleDownload(activeReport.id, 'xlsx')}>
                      <Download size={13} /> Excel (.xlsx)
                    </button>
                  </div>
                </div>

                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {/* Title Banner */}
                  <div style={{
                    background: 'var(--canvas-bg)',
                    border: '1.5px solid var(--border-ink)',
                    borderRadius: 10,
                    padding: 16,
                    boxShadow: 'var(--shadow-solid-sm)'
                  }}>
                    <div style={{ fontFamily: 'var(--font-serif)', fontSize: 17, fontWeight: 800, color: 'var(--brand-blue)', marginBottom: 4 }}>
                      {activeReport.data_source === 'cloudiq_pulse' ? 'CloudIQ Pulse Infrastructure Monitoring Report' : 'AWS CloudWatch Executive Monitoring Report'}
                    </div>
                    <div className="font-mono" style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 8 }}>
                      Account: <strong>{activeReport.client_name}</strong> | AWS ID: <strong>{activeReport.account_id}</strong> | Date: <strong>{activeReport.report_date}</strong>
                    </div>
                    <div style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 700,
                      background: activeReport.banner_msg?.includes('URGENT') || activeReport.banner_msg?.includes('ALARM') ? 'var(--brand-coral-bg)' : '#ECFDF5',
                      color: activeReport.banner_msg?.includes('URGENT') || activeReport.banner_msg?.includes('ALARM') ? '#9A2C14' : '#047857',
                      border: '1px solid var(--border-ink)'
                    }}>
                      {activeReport.banner_msg || 'All metrics operating within normal threshold limits.'}
                    </div>
                  </div>

                  {/* Exec Summary */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <Sparkles size={16} color="var(--brand-blue)" />
                      <h4 style={{ fontFamily: 'var(--font-serif)', fontSize: 14, fontWeight: 800, color: 'var(--ink-900)', margin: 0, textTransform: 'uppercase' }}>
                        1. Executive Summary
                      </h4>
                    </div>
                    <div style={{
                      padding: 14,
                      background: 'var(--canvas-bg)',
                      borderRadius: 8,
                      border: '1px solid var(--border-ink)',
                      fontSize: 13,
                      color: 'var(--ink-800)',
                      lineHeight: 1.6
                    }}>
                      {activeReport.exec_summary || 'No executive summary available.'}
                    </div>
                  </div>

                  {/* Account Details */}
                  <div style={{
                    padding: 14,
                    background: 'var(--canvas-bg)',
                    borderRadius: 8,
                    border: '1px solid var(--border-ink)'
                  }}>
                    <h4 style={{ fontFamily: 'var(--font-serif)', fontSize: 13.5, fontWeight: 800, color: 'var(--ink-900)', margin: '0 0 6px 0', textTransform: 'uppercase' }}>
                      2. Account & Alarm Synthesis
                    </h4>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-700)' }}>
                      {/* Region comes from the client record. It was hardcoded to
                          ap-south-1 regardless of where the account actually runs. */}
                      Account ID: <strong>{activeReport.account_id || 'not recorded'}</strong>
                      {' • '}Region: <strong>{activeReport.aws_region || 'not recorded'}</strong>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 4 }}>
                      Alarm Summary: <strong>{activeReport.alarm_summary || 'Normal'}</strong>
                    </div>
                  </div>

                  {/* EC2 Metrics */}
                  {activeReport.metrics?.length > 0 && (
                    <div>
                      <h4 style={{ fontFamily: 'var(--font-serif)', fontSize: 13.5, fontWeight: 800, color: 'var(--ink-900)', margin: '0 0 8px 0', textTransform: 'uppercase' }}>
                        2.1 EC2 Infrastructure Fleet Metrics
                      </h4>
                      <div style={{ overflowX: 'auto', border: '1.5px solid var(--border-ink)', borderRadius: 8 }}>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Instance Name</th>
                              <th>Instance ID</th>
                              <th>CPU</th>
                              <th>Memory</th>
                              <th>Disk</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeReport.metrics.map((m, idx) => (
                              <tr key={idx}>
                                <td style={{ fontWeight: 700, color: 'var(--ink-900)' }}>{m.name}</td>
                                <td className="font-mono text-xs text-muted">{m.instance_id || m.type}</td>
                                <td>{m.cpu || 'N/A'}</td>
                                <td>{m.mem || 'N/A'}</td>
                                <td>{m.disk || 'N/A'}</td>
                                <td>
                                  <span className={`badge ${String(m.alarm).includes('OK') ? 'badge-ok' : 'badge-alarm'}`}>
                                    {m.alarm || 'OK'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Observations */}
                  {activeReport.observations?.length > 0 && (
                    <div>
                      <h4 style={{ fontFamily: 'var(--font-serif)', fontSize: 13.5, fontWeight: 800, color: 'var(--ink-900)', margin: '0 0 8px 0', textTransform: 'uppercase' }}>
                        3. Strategic Observations & Recommendations
                      </h4>
                      <div style={{ overflowX: 'auto', border: '1.5px solid var(--border-ink)', borderRadius: 8 }}>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Domain / Topic</th>
                              <th>Observation Detail</th>
                              <th>Priority</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeReport.observations.map((obs, idx) => (
                              <tr key={idx}>
                                <td style={{ fontWeight: 800 }}>{idx + 1}</td>
                                <td style={{ fontWeight: 700, color: 'var(--ink-900)' }}>{obs.topic}</td>
                                <td style={{ fontSize: 12.5, color: 'var(--ink-700)' }}>{obs.text}</td>
                                <td>
                                  <span className={`badge ${obs.priority === 'URGENT' ? 'badge-alarm' : (obs.priority === 'Monitor' ? 'badge-warning' : 'badge-ok')}`}>
                                    {obs.priority}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Sign-off & Dispatch Bar */}
                  <div style={{
                    padding: 16,
                    background: 'var(--canvas-bg)',
                    borderRadius: 10,
                    border: '1.5px solid var(--border-ink)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 12,
                    boxShadow: 'var(--shadow-solid-sm)'
                  }}>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink-900)' }}>
                        Human-in-the-loop sign-off
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>
                        {activeReport.status === 'dispatched' ? (
                          <>
                            Sent{activeReport.dispatched_at ? ` on ${activeReport.dispatched_at}` : ''}
                            {activeReport.dispatch_recipients
                              ? ` to ${activeReport.dispatch_recipients}`
                              : ''}
                            .
                          </>
                        ) : activeReport.status === 'approved' ? (
                          <>
                            Approved by {activeReport.approved_by || 'an operator'}
                            {activeReport.approved_at ? ` on ${activeReport.approved_at}` : ''}. Ready
                            to send to {activeReport.recipient_count || 0} recipient(s).
                          </>
                        ) : activeReport.status === 'dispatch_failed' ? (
                          <>Delivery failed. Review the error and try again.</>
                        ) : (
                          <>Review the content above, then approve it before it can be sent.</>
                        )}
                      </div>
                      {activeReport.dispatch_error && (
                        <div
                          role="alert"
                          style={{ fontSize: 12, color: 'var(--brand-coral)', fontWeight: 700, marginTop: 6 }}
                        >
                          {activeReport.dispatch_error}
                        </div>
                      )}
                      {lifecycleError && (
                        <div
                          role="alert"
                          style={{ fontSize: 12, color: 'var(--brand-coral)', fontWeight: 700, marginTop: 6 }}
                        >
                          {lifecycleError}
                        </div>
                      )}
                      {lifecycleNotice && (
                        <div
                          role="status"
                          style={{ fontSize: 12, color: '#065F46', fontWeight: 700, marginTop: 6 }}
                        >
                          {lifecycleNotice}
                        </div>
                      )}
                    </div>

                    {/* Two real transitions: approve records the sign-off, dispatch
                        e-mails the artefacts. This was previously a single button
                        wired to an alert() that changed nothing. */}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={
                          lifecycleBusy
                          || activeReport.status === 'approved'
                          || activeReport.status === 'dispatched'
                        }
                        onClick={handleApprove}
                      >
                        <CheckCircle2 size={15} aria-hidden="true" />
                        {activeReport.status === 'approved' || activeReport.status === 'dispatched'
                          ? 'Approved'
                          : lifecycleBusy === 'approve'
                            ? 'Approving…'
                            : 'Approve'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={
                          lifecycleBusy
                          || activeReport.status === 'dispatched'
                          || activeReport.status === 'draft'
                        }
                        onClick={handleDispatch}
                        title={
                          activeReport.status === 'draft'
                            ? 'Approve the report before sending it'
                            : undefined
                        }
                      >
                        <Send size={15} aria-hidden="true" />
                        {activeReport.status === 'dispatched'
                          ? 'Sent'
                          : lifecycleBusy === 'dispatch'
                            ? 'Sending…'
                            : 'Send to client'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Report Archive Sidebar */}
          <div>
            <div className="card">
              <div className="card-header">
                <h3>Report Archive ({reports.length})</h3>
              </div>
              <div className="card-body" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 600, overflowY: 'auto' }}>
                {reports.map((rep) => {
                  const isSelected = activeReport?.id === rep.id
                  return (
                    <div
                      key={rep.id}
                      onClick={() => {
                        setActiveReportId(rep.id)
                        setLifecycleError(null)
                        setLifecycleNotice(null)
                      }}
                      style={{
                        padding: '12px 14px',
                        borderRadius: 8,
                        background: isSelected ? 'var(--brand-blue-subtle)' : '#FFFFFF',
                        border: isSelected ? '2px solid var(--brand-blue)' : '1.5px solid var(--border-ink)',
                        boxShadow: isSelected ? 'var(--shadow-solid-blue)' : 'var(--shadow-solid-sm)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink-900)' }}>{rep.client_name}</span>
                        <span className={`badge ${rep.status === 'sent' ? 'badge-ok' : 'badge-warning'}`} style={{ fontSize: 9.5 }}>
                          {rep.status === 'sent' ? 'Sent' : 'Draft'}
                        </span>
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>
                        {rep.report_date} • {rep.exec_mode === 'browser_session' ? 'CDP Automation' : (rep.data_source === 'cloudiq_pulse' ? 'CloudIQ Pulse Fleet' : 'Live CloudWatch API')}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generation Modal */}
      {showGenModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2>Generate a report from live telemetry</h2>
              <button
                type="button"
                className="btn-icon"
                aria-label="Close the report generation dialog"
                onClick={() => { if (!generating) { setShowGenModal(false); setGenError(null) } }}
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Select Client Account</label>
                <select
                  className="form-control"
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  disabled={generating}
                >
                  <option value="">-- Choose Client Account --</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.client_name} (AWS: {c.account_id})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Data Ingestion Engine</label>
                <select
                  className="form-control"
                  value={execMode}
                  onChange={(e) => setExecMode(e.target.value)}
                  disabled={generating}
                >
                  <option value="live_api">AWS Live Telemetry & Synthesis (Recommended)</option>
                  <option value="operator_upload">Multi-Source Log Upload & Operational Telemetry</option>
                </select>
              </div>

              {generating && (
                <div style={{
                  padding: 14,
                  background: 'var(--canvas-bg)',
                  borderRadius: 8,
                  border: '1.5px solid var(--border-ink)',
                  marginTop: 14
                }}>
                  {/* Honest indeterminate progress. The stage labels here were
                      driven by 1.5s/3s timers rather than by the backend, so they
                      described work that had not necessarily started. */}
                  <div
                    role="status"
                    aria-busy="true"
                    aria-live="polite"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}
                  >
                    <Loader size={16} aria-hidden="true" className="spin" style={{ color: 'var(--brand-blue)' }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-blue)' }}>
                      Collecting telemetry and compiling the report…
                    </span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-500)' }}>
                    This queries CloudWatch metrics, alarms and Logs Insights for every discovered
                    resource, then renders the Word and Excel documents. Large accounts can take a
                    minute or more. Keep this dialog open.
                  </div>
                </div>
              )}

              {genError && (
                <div style={{
                  marginTop: 14,
                  padding: 10,
                  background: 'var(--brand-coral-bg)',
                  borderRadius: 6,
                  border: '1px solid var(--border-ink)',
                  color: '#9A2C14',
                  fontSize: 12.5,
                  fontWeight: 600
                }}>
                  Error: {genError}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setShowGenModal(false); setGenError(null) }}
                disabled={generating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStartGeneration}
                disabled={generating || !selectedClient}
              >
                {generating ? <Loader size={15} className="spin" /> : <Play size={15} />}
                {generating ? 'Running Pipeline...' : 'Start Generation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
